import { type Locale } from '@syncanix/shared';
import { cn } from '@syncanix/ui';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { LinkPanel } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { localizedPath } from '@/i18n/locale-path';

import { getDocNeighbors, getSectionForSlug } from './content/docs-nav';
import { DocsSidebar } from './DocsSidebar';
import { useActiveHeading } from './use-active-heading';

import type { ReactElement, ReactNode } from 'react';

/** A table-of-contents entry for the current page. */
export interface TocHeading {
  readonly id: string;
  readonly textKey: string;
}

/**
 * The documentation shell: a searchable, collapsible section sidebar, a
 * breadcrumb, the page body, a scrollspy "on this page" rail, and a prev/next
 * pager. RTL-correct (logical properties throughout) and responsive (the
 * sidebar collapses to a disclosure on mobile). Both sticky rails cap their
 * height at the viewport and scroll internally — a rail taller than the screen
 * must never trap its lower entries out of reach.
 */
export function DocsLayout({
  locale,
  currentSlug,
  headings,
  children,
}: {
  readonly locale: Locale;
  readonly currentSlug?: string;
  readonly headings?: readonly TocHeading[];
  readonly children: ReactNode;
}): ReactElement {
  const { t, locale: ctxLocale, setLocale } = useT();
  const mobileNavRef = useRef<HTMLDetailsElement>(null);

  // Keep the i18n locale in sync with the URL on client-side navigation between
  // locale variants (the language switcher navigates; SSR/hydration already match).
  useEffect(() => {
    if (locale !== ctxLocale) setLocale(locale);
  }, [locale, ctxLocale, setLocale]);

  const hasToc = headings !== undefined && headings.length > 0;

  return (
    <div className="mx-auto w-full max-w-7xl px-6 py-8">
      {/* The third track is conditional, not always-on. A static
          `xl:grid-cols-[16rem_1fr_13rem]` reserved the 13rem TOC column plus its
          48px gap on every page that renders no TOC — 256px of empty gutter on
          the docs index, where the directory then had to squeeze its columns to
          fit around nothing. */}
      <div
        className={cn(
          'lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-10 xl:gap-12',
          hasToc && 'xl:grid-cols-[16rem_minmax(0,1fr)_13rem]',
        )}
      >
        {/* Sidebar: a disclosure on mobile, a sticky scrollable rail on desktop. */}
        <details
          ref={mobileNavRef}
          className="mb-6 rounded-[var(--bd-radius)] border border-[color:var(--bd-line)] bg-[color:var(--bd-raise)] p-4 lg:hidden"
        >
          <summary className="cursor-pointer text-sm font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
            {t('docs.nav.browse')}
          </summary>
          <div className="mt-4">
            <DocsSidebar
              locale={locale}
              currentSlug={currentSlug}
              onNavigate={() => mobileNavRef.current?.removeAttribute('open')}
            />
          </div>
        </details>
        <aside className="hidden lg:block">
          <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain pb-8 pe-1">
            <DocsSidebar locale={locale} currentSlug={currentSlug} withSlashShortcut />
          </div>
        </aside>

        <main id="main" tabIndex={-1} className="min-w-0">
          <Breadcrumb locale={locale} currentSlug={currentSlug} />
          {children}
          {currentSlug !== undefined ? <Pager locale={locale} currentSlug={currentSlug} /> : null}
        </main>

        {hasToc ? (
          <aside className="hidden xl:block">
            <Toc headings={headings} />
          </aside>
        ) : null}
      </div>
    </div>
  );
}

/** The "on this page" rail, with a scrollspy highlight on the current heading. */
function Toc({ headings }: { readonly headings: readonly TocHeading[] }): ReactElement {
  const { t } = useT();
  const activeId = useActiveHeading(headings.map((h) => h.id));
  return (
    <nav
      aria-label={t('docs.nav.onThisPage')}
      className="sticky top-24 flex max-h-[calc(100vh-7rem)] flex-col gap-2 overflow-y-auto overscroll-contain pb-8"
    >
      {/* `rtl:` resets: Arabic and Hebrew have no letter case, and wide tracking
          breaks Arabic's cursive joining — so the mono eyebrow is an LTR-only
          device and degrades to a plain small label in RTL. */}
      <p className="font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
        {t('docs.nav.onThisPage')}
      </p>
      <ul className="flex flex-col border-s border-[color:var(--bd-line)] text-[0.83rem] leading-[1.45]">
        {headings.map((h) => {
          const active = h.id === activeId;
          return (
            <li key={h.id} className="-ms-px">
              <a
                href={`#${h.id}`}
                aria-current={active ? 'location' : undefined}
                className={cn(
                  'block border-s-2 py-1.5 ps-3 transition-colors duration-200',
                  active
                    ? 'border-[color:var(--bd-blue)] font-medium text-[color:var(--bd-ink)]'
                    : 'border-transparent text-[color:var(--bd-muted)] hover:border-[color:var(--bd-dot)] hover:text-[color:var(--bd-ink)]',
                )}
              >
                {t(h.textKey)}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function Breadcrumb({
  locale,
  currentSlug,
}: {
  readonly locale: Locale;
  readonly currentSlug: string | undefined;
}): ReactElement {
  const { t } = useT();
  const section = currentSlug !== undefined ? getSectionForSlug(currentSlug) : undefined;
  const page = section?.pages.find((p) => p.slug === currentSlug);
  const linkClass =
    'text-[color:var(--bd-muted)] transition-colors hover:text-[color:var(--bd-ink)]';
  const divider = (
    <li aria-hidden="true" className="select-none text-[color:var(--bd-subtle)]">
      /
    </li>
  );
  return (
    <nav aria-label={t('docs.nav.breadcrumbLabel')} className="mb-5 text-[0.8rem]">
      <ol className="flex flex-wrap items-center gap-1.5">
        <li>
          <Link to={localizedPath('/', locale)} className={linkClass}>
            {t('docs.nav.home')}
          </Link>
        </li>
        {divider}
        <li>
          <Link to={localizedPath('/docs', locale)} className={linkClass}>
            {t('docs.nav.documentation')}
          </Link>
        </li>
        {section !== undefined ? (
          <>
            {divider}
            <li className="text-[color:var(--bd-muted)]">{t(section.titleKey)}</li>
          </>
        ) : null}
        {page !== undefined ? (
          <>
            {divider}
            <li aria-current="page" className="font-medium text-[color:var(--bd-ink)]">
              {t(page.titleKey)}
            </li>
          </>
        ) : null}
      </ol>
    </nav>
  );
}

function Pager({
  locale,
  currentSlug,
}: {
  readonly locale: Locale;
  readonly currentSlug: string;
}): ReactElement {
  const { t } = useT();
  const { prev, next } = getDocNeighbors(currentSlug);
  // The bedrock `LinkPanel` — the same card the doc body's `cardGrid` uses, so
  // the pager reads as one more pair of destinations rather than as a different
  // kind of chrome.
  const labelClass =
    'font-mono rtl:font-sans text-[10px] uppercase tracking-[0.14em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal';
  return (
    <nav
      aria-label={t('docs.nav.pagerLabel')}
      className="mt-14 flex flex-col gap-4 border-t border-[color:var(--bd-line)] pt-7 sm:flex-row sm:justify-between"
    >
      {prev !== undefined ? (
        <LinkPanel
          href={localizedPath(`/docs/${prev.slug}`, locale)}
          className="flex flex-col gap-1 p-4 sm:w-1/2"
        >
          <span className={labelClass}>{t('docs.nav.previous')}</span>
          <span className="font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
            {t(prev.titleKey)}
          </span>
          <span className="text-xs text-[color:var(--bd-muted)]">
            {t(getSectionForSlug(prev.slug)?.titleKey ?? '')}
          </span>
        </LinkPanel>
      ) : (
        <span className="sm:w-1/2" />
      )}
      {next !== undefined ? (
        <LinkPanel
          href={localizedPath(`/docs/${next.slug}`, locale)}
          className="flex flex-col gap-1 p-4 text-end sm:w-1/2"
        >
          <span className={labelClass}>{t('docs.nav.next')}</span>
          <span className="font-semibold tracking-[-0.02em] text-[color:var(--bd-ink)]">
            {t(next.titleKey)}
          </span>
          <span className="text-xs text-[color:var(--bd-muted)]">
            {t(getSectionForSlug(next.slug)?.titleKey ?? '')}
          </span>
        </LinkPanel>
      ) : (
        <span className="sm:w-1/2" />
      )}
    </nav>
  );
}
